type SiteRuntimeConfig = {
  origin?: string;
  plausibleDomain?: string;
};

function runtimeConfig(): SiteRuntimeConfig {
  if (typeof window === "undefined") return {};
  return window.__SITE_CONFIG__ ?? {};
}

function resolveOrigin(): string {
  const configured = runtimeConfig().origin?.trim();
  if (configured) return configured.replace(/\/$/, "");
  if (typeof window !== "undefined" && window.location?.origin) {
    return window.location.origin;
  }
  return (import.meta.env.VITE_SITE_URL || "https://jayvanzyl.me").replace(/\/$/, "");
}

function resolvePlausibleDomain(): string {
  if (typeof window !== "undefined" && window.__SITE_CONFIG__) {
    return window.__SITE_CONFIG__.plausibleDomain?.trim() ?? "";
  }
  return import.meta.env.VITE_PLAUSIBLE_DOMAIN || "";
}

export const site = {
  name: "Jay van Zyl",
  jobTitle: "Founder & Head of AI Platform",
  worksFor: "ecosystem.Ai",
  legalEntity: "Ecogenetic LLC",
  tagline: "Human systems and production platforms",
  proposition:
    "The next action depends on the relationship, and the platform has to score that while the person is still acting.",
  description:
    "Founder and Head of AI Platform at ecosystem.Ai. Computational social scientist and product-line practitioner. Author of Built to Thrive and Process Innovation Imperative.",
  location: "San Francisco and Cape Town",
  alumniOf: "University of the Witwatersrand",
  get origin() {
    return resolveOrigin();
  },
  get plausibleDomain() {
    return resolvePlausibleDomain();
  },
  emailDisplay: "jay@ecosystem.ai",
  portraits: {
    primary: {
      src: "/images/jay-2018.jpg",
      alt: "Portrait of Jay van Zyl, 2018",
      width: 800,
      height: 1000,
    },
    home: {
      src: "/images/jay-2025.jpg",
      alt: "Portrait of Jay van Zyl",
      width: 820,
      height: 790,
    },
  },
  profiles: {
    linkedin: "https://www.linkedin.com/in/jayvanzyl/",
    github: "https://github.com/jayvanzyl",
    githubOrg: "https://github.com/ecogenetic",
    githubBrand: "https://github.com/ecosystemai",
    academia: "https://independent.academia.edu/JayvanZyl",
    researchgate: "https://www.researchgate.net/profile/Jay-Van-Zyl",
    ecosystem: "https://ecosystem.ai",
    ecosystemCode: "https://ecosystemcode.com",
    developer: "https://developer.ecosystem.ai",
    ontology: "https://ontology.ecosystem.ai",
    demo: "https://medemo.ecosystem.ai",
    gpts: "https://gpts.ecosystem.ai",
    youtubeJay:
      "https://www.youtube.com/results?search_query=Jay+van+Zyl",
    youtubeEcosystem:
      "https://www.youtube.com/results?search_query=ecosystem.ai",
  },
  knowsAbout: [
    "computational social science",
    "behavioral prediction",
    "network analysis",
    "platform architecture",
    "product-line practice",
    "machine learning in production",
  ],
} as const;

export type NavItem = {
  to: string;
  label: string;
};

export const primaryNav: NavItem[] = [
  { to: "/work", label: "Projects" },
  { to: "/building", label: "Building" },
  { to: "/thinking", label: "Thinking" },
  { to: "/resources", label: "Resources" },
  { to: "/about", label: "About" },
];

export const sameAs = [
  site.profiles.linkedin,
  site.profiles.github,
  site.profiles.githubOrg,
  site.profiles.ecosystem,
  site.profiles.ecosystemCode,
  site.profiles.academia,
  site.profiles.researchgate,
];
